import {
  CHUNK_SIZE,
  chunkKeyInt,
  createNoiseGenerator,
  TILE_SIZE,
  TileType,
  type TileTypeValue,
} from '@dreamgame/shared';
import { Container, Graphics } from 'pixi.js';
import { type ChunkWithObjects, getAllChunks } from '../../state/chunks';
import { onCategoryReload } from '../TextureManager';
import type { VisibleChunk } from './types';

// ============================================================================
// ForestDetailLayer - Procedural textured forest-floor decals
// ============================================================================
// Renders lightweight procedural texture splats (leaf litter, moss, twigs,
// pebbles, tiny flowers) on forest tiles. Placement is deterministic and
// patchy to avoid grid-like repetition.
// ============================================================================

let container: Container;

const FOREST_TILES: ReadonlySet<TileTypeValue> = new Set([
  TileType.FOREST_FLOOR,
  TileType.FOREST_GRASS,
  TileType.PINE_NEEDLES,
]);

const DETAIL_COLORS = [0x5d7a3f, 0x4f6b37, 0x7a6842, 0x8b7e62, 0x95a24f, 0x735a3f] as const;

// Decal sizing/opacity in world pixels
const DETAIL_SIZE_MIN = 2;
const DETAIL_SIZE_RANGE = 7;
const DETAIL_ALPHA_MIN = 0.18;
const DETAIL_ALPHA_RANGE = 0.34;

// Per-chunk cache
interface CachedChunkEntry {
  graphics: Graphics;
  tilesRef: Uint8Array;
  detailRevision: number;
}

const _chunkCache = new Map<number, CachedChunkEntry>();
const _usedKeys = new Set<number>();
const _chunkKeyTemp = { x: 0, y: 0 };

// Low-frequency fields for organic patchiness
const _coverageNoise = createNoiseGenerator({ seed: 9503 });
const _clusterNoise = createNoiseGenerator({ seed: 9511 });

let _detailRevision = 0;
let _cachedChunksRef: ReadonlyMap<number, ChunkWithObjects> | null = null;
let _cachedChunksSize = 0;
let _cachedVisibleHash = 0;

const detailHash = ({
  tileX,
  tileY,
  seed,
}: {
  tileX: number;
  tileY: number;
  seed: number;
}): number => {
  return (((tileX * 73856093 + seed * 83492791) ^ (tileY * 19349663)) >>> 0) % 1000;
};

const coverageValue = ({
  worldTileX,
  worldTileY,
}: {
  worldTileX: number;
  worldTileY: number;
}): number => {
  return (_coverageNoise.noise2D({ x: worldTileX * 0.08, y: worldTileY * 0.08 }) + 1) / 2;
};

const clusterValue = ({
  worldTileX,
  worldTileY,
}: {
  worldTileX: number;
  worldTileY: number;
}): number => {
  return (_clusterNoise.noise2D({ x: worldTileX * 0.22, y: worldTileY * 0.22 }) + 1) / 2;
};

const computeVisibleHash = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): number => {
  let hash = visibleChunks.length;
  for (let i = 0; i < visibleChunks.length; i++) {
    const vc = visibleChunks[i];
    if (vc) {
      hash = (hash * 31 + vc.x) | 0;
      hash = (hash * 31 + vc.y) | 0;
    }
  }
  return hash;
};

export const createForestDetailLayer = (): Container => {
  container = new Container();
  return container;
};

export const loadForestDetailTextures = async (): Promise<void> => {
  // Procedural details don't load external textures; increment revision to rebuild chunks.
  _detailRevision++;
};

const invalidateForestDetailCache = async (): Promise<void> => {
  await loadForestDetailTextures();
  for (const entry of _chunkCache.values()) {
    entry.tilesRef = new Uint8Array(0);
    entry.detailRevision = -1;
  }
};

onCategoryReload({ category: 'tiles', callback: invalidateForestDetailCache });

const addTileDecals = ({
  graphics,
  worldTileX,
  worldTileY,
  worldX,
  worldY,
}: {
  graphics: Graphics;
  worldTileX: number;
  worldTileY: number;
  worldX: number;
  worldY: number;
}): void => {
  const coverage = coverageValue({ worldTileX, worldTileY });
  const cluster = clusterValue({ worldTileX, worldTileY });

  const spawnGate = detailHash({ tileX: worldTileX, tileY: worldTileY, seed: 41 });
  const spawnThreshold = 120 + Math.floor(coverage * 260);
  if (spawnGate > spawnThreshold) {
    return;
  }

  const baseCount = detailHash({ tileX: worldTileX, tileY: worldTileY, seed: 43 }) % 3;
  const bonusCount =
    cluster > 0.72 && detailHash({ tileX: worldTileX, tileY: worldTileY, seed: 47 }) < 340 ? 1 : 0;
  const denseCount =
    coverage > 0.78 && detailHash({ tileX: worldTileX, tileY: worldTileY, seed: 53 }) < 180 ? 1 : 0;
  const decalCount = Math.min(5, 1 + baseCount + bonusCount + denseCount);

  for (let i = 0; i < decalCount; i += 1) {
    const px = detailHash({ tileX: worldTileX, tileY: worldTileY, seed: i * 17 + 67 });
    const py = detailHash({ tileX: worldTileX, tileY: worldTileY, seed: i * 17 + 71 });
    const si = detailHash({ tileX: worldTileX, tileY: worldTileY, seed: i * 17 + 73 });
    const ai = detailHash({ tileX: worldTileX, tileY: worldTileY, seed: i * 17 + 79 });
    const ti = detailHash({ tileX: worldTileX, tileY: worldTileY, seed: i * 17 + 89 });
    const shape = detailHash({ tileX: worldTileX, tileY: worldTileY, seed: i * 17 + 97 }) % 5;

    const x = worldX + (px % (TILE_SIZE - 4)) + 2;
    const y = worldY + (py % (TILE_SIZE - 4)) + 2;
    const size = DETAIL_SIZE_MIN + (si % DETAIL_SIZE_RANGE);
    const alpha = DETAIL_ALPHA_MIN + (ai / 1000) * DETAIL_ALPHA_RANGE;
    const color = DETAIL_COLORS[ti % DETAIL_COLORS.length] ?? DETAIL_COLORS[0];

    drawDetailShape({
      graphics,
      x,
      y,
      size,
      alpha,
      color,
      shape,
    });
  }
};

const drawDetailShape = ({
  graphics,
  x,
  y,
  size,
  alpha,
  color,
  shape,
}: {
  graphics: Graphics;
  x: number;
  y: number;
  size: number;
  alpha: number;
  color: number;
  shape: number;
}): void => {
  graphics.setFillStyle({ color, alpha });
  if (shape === 0) {
    graphics.circle(x, y, size * 0.35);
    graphics.fill();
    return;
  }
  if (shape === 1) {
    graphics.ellipse(x, y, size * 0.5, size * 0.3);
    graphics.fill();
    return;
  }
  if (shape === 2) {
    graphics.rect(x - size * 0.35, y - size * 0.2, size * 0.7, size * 0.4);
    graphics.fill();
    return;
  }
  if (shape === 3) {
    graphics.rect(x - 0.5, y - size * 0.4, 1, size * 0.8);
    graphics.fill();
    graphics.rect(x + 1.5, y - size * 0.25, 1, size * 0.6);
    graphics.fill();
    return;
  }

  graphics.ellipse(x, y, size * 0.45, size * 0.45);
  graphics.fill();
  graphics.setFillStyle({ color: 0x2d3a22, alpha: alpha * 0.25 });
  graphics.ellipse(x + 0.8, y + 0.6, size * 0.3, size * 0.28);
  graphics.fill();
};

const rebuildChunkDetails = ({
  graphics,
  chunk,
  chunkX,
  chunkY,
}: {
  graphics: Graphics;
  chunk: ChunkWithObjects;
  chunkX: number;
  chunkY: number;
}): void => {
  graphics.clear();

  const chunkWorldX = chunkX * CHUNK_SIZE * TILE_SIZE;
  const chunkWorldY = chunkY * CHUNK_SIZE * TILE_SIZE;

  for (let localY = 0; localY < CHUNK_SIZE; localY++) {
    for (let localX = 0; localX < CHUNK_SIZE; localX++) {
      const tileIndex = localY * CHUNK_SIZE + localX;
      const tileType = (chunk.tiles[tileIndex] ?? TileType.VOID) as TileTypeValue;
      if (!FOREST_TILES.has(tileType)) {
        continue;
      }

      const worldTileX = chunkX * CHUNK_SIZE + localX;
      const worldTileY = chunkY * CHUNK_SIZE + localY;
      const worldX = chunkWorldX + localX * TILE_SIZE;
      const worldY = chunkWorldY + localY * TILE_SIZE;

      addTileDecals({
        graphics,
        worldTileX,
        worldTileY,
        worldX,
        worldY,
      });
    }
  }
};

const ensureChunkDetails = ({
  chunkKey,
  chunk,
  chunkX,
  chunkY,
}: {
  chunkKey: number;
  chunk: ChunkWithObjects;
  chunkX: number;
  chunkY: number;
}): void => {
  let cached = _chunkCache.get(chunkKey);
  if (!cached) {
    const graphics = new Graphics();
    container.addChild(graphics);
    rebuildChunkDetails({ graphics, chunk, chunkX, chunkY });
    cached = {
      graphics,
      tilesRef: chunk.tiles,
      detailRevision: _detailRevision,
    };
    _chunkCache.set(chunkKey, cached);
  } else if (cached.tilesRef !== chunk.tiles || cached.detailRevision !== _detailRevision) {
    rebuildChunkDetails({ graphics: cached.graphics, chunk, chunkX, chunkY });
    cached.tilesRef = chunk.tiles;
    cached.detailRevision = _detailRevision;
  }

  cached.graphics.visible = true;
};

const rebuildVisibleDetails = ({
  visibleChunks,
  loadedChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
}): void => {
  _usedKeys.clear();

  for (const { x: chunkX, y: chunkY } of visibleChunks) {
    _chunkKeyTemp.x = chunkX;
    _chunkKeyTemp.y = chunkY;
    const chunkKey = chunkKeyInt(_chunkKeyTemp);
    _usedKeys.add(chunkKey);
    const chunk = loadedChunks.get(chunkKey);
    if (!chunk) {
      continue;
    }
    ensureChunkDetails({ chunkKey, chunk, chunkX, chunkY });
  }

  for (const [key, entry] of _chunkCache) {
    if (!_usedKeys.has(key)) {
      entry.graphics.visible = false;
    }
  }
};

export const drawForestDetails = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  const loadedChunks = getAllChunks();
  const visibleHash = computeVisibleHash({ visibleChunks });
  const needsRebuild =
    loadedChunks !== _cachedChunksRef ||
    loadedChunks.size !== _cachedChunksSize ||
    visibleHash !== _cachedVisibleHash;

  if (!needsRebuild) {
    return;
  }

  rebuildVisibleDetails({ visibleChunks, loadedChunks });
  _cachedChunksRef = loadedChunks;
  _cachedChunksSize = loadedChunks.size;
  _cachedVisibleHash = visibleHash;
};
